/**
 * API Layer - Cloud Function Calls
 *
 * IMPORTANT: All callables MUST be invoked via httpsCallable() only.
 * Do NOT use fetch(), axios, or any direct HTTP request to cloudfunctions.net.
 * The Firebase SDK attaches the auth token and uses the correct protocol.
 */

import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";
import {
  CreateEnrollmentRequest,
  CreateEnrollmentResponse,
  CreateEnrollmentAsStudentRequest,
  CreateEnrollmentAsStudentResponse,
  CreateClassRequest,
  CreateClassResponse,
  UpdateClassRequest,
  UpdateClassResponse,
  DeleteEnrollmentRequest,
  ListStudentsForAdminResponse,
  ListParentsForAdminResponse,
  GetClassesWithEnrollmentsResponse,
  SubmitRegistrationRequest,
  SubmitRegistrationResponse,
  GetRegistrationForRestoreResponse,
  ListRegistrationsRequest,
  ListRegistrationsResponse,
  ListTransactionsRequest,
  ListTransactionsResponse,
  DeleteClassRequest,
  DeleteClassResponse,
  GetClassesForForumResponse,
  GetPendingByRegistrationIdResponse,
  UpdateRegistrationFiscalDataRequest,
  UpdateRegistrationFiscalDataResponse,
  UpdateRegistrationClassesRequest,
  UpdateRegistrationClassesResponse,
  UpdateRegistrationPaymentMethodRequest,
  UpdateRegistrationPaymentMethodResponse,
  GetStudentWithEnrollmentsResponse,
  GetCurrentClassesByStudentCodeResponse,
  CheckStudentCodeAvailableResponse,
  CreateAzulPaymentSessionResponse,
  ClassSelectionWindowResponse,
  GetActiveRegistrationMonthResponse,
  SetActiveRegistrationMonthResponse,
  GetBillingCyclesForAdminRequest,
  GetBillingCyclesForAdminResponse,
  GetBillingCyclesAndPaymentsResponse,
  AdminSettings,
  GetAdminSettingsResponse,
  UpdateAdminSettingsResponse,
  GetMyRegistrationsResponse,
  LookupStudentForParentResponse,
} from "../types";

// =============================================================================
// PARENT PORTAL — MY REGISTRATIONS (requires auth)
// =============================================================================

export const lookupStudentForParent = async (
  cardCode: string,
  parentEmail: string
): Promise<LookupStudentForParentResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<{ cardCode: string; parentEmail: string }, LookupStudentForParentResponse>(
    functions,
    "lookupStudentForParent"
  );
  const result = await fn({ cardCode: cardCode.trim(), parentEmail: parentEmail.trim() });
  return result.data;
};

export const getMyRegistrations = async (): Promise<GetMyRegistrationsResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<Record<string, never>, GetMyRegistrationsResponse>(
    functions,
    "getMyRegistrations"
  );
  const result = await fn({});
  return result.data;
};

// =============================================================================
// PUBLIC REGISTRATION (no auth - Talent School form)
// =============================================================================

export const submitRegistration = async (
  data: SubmitRegistrationRequest
): Promise<SubmitRegistrationResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<SubmitRegistrationRequest, SubmitRegistrationResponse>(
    functions,
    "submitRegistration"
  );
  const result = await fn(data);
  return result.data;
};

export const listRegistrations = async (
  data?: ListRegistrationsRequest
): Promise<ListRegistrationsResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<ListRegistrationsRequest | undefined, ListRegistrationsResponse>(
    functions,
    "listRegistrations"
  );
  const result = await fn(data ?? {});
  return result.data;
};

export const listTransactions = async (
  data: ListTransactionsRequest
): Promise<ListTransactionsResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<ListTransactionsRequest, ListTransactionsResponse>(
    functions,
    "listTransactions"
  );
  const result = await fn(data);
  return result.data;
};

/** Admin Finanzas: list billing cycles and totals (billingCycles collection only). */
export const getBillingCyclesForAdmin = async (
  data?: GetBillingCyclesForAdminRequest
): Promise<GetBillingCyclesForAdminResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<
    GetBillingCyclesForAdminRequest | undefined,
    GetBillingCyclesForAdminResponse
  >(functions, "getBillingCyclesForAdmin");
  const result = await fn(data ?? {});
  return result.data;
};

/** Admin Finanzas: raw billingCycles + payments for client-side join and status computation. */
export const getBillingCyclesAndPaymentsForAdmin = async (): Promise<GetBillingCyclesAndPaymentsResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<unknown, GetBillingCyclesAndPaymentsResponse>(
    functions,
    "getBillingCyclesAndPaymentsForAdmin"
  );
  const result = await fn({});
  return result.data;
};

/** Get current class IDs for a student by institutional code (renewal: pre-fill class selection). */
export const getCurrentClassesByStudentCode = async (
  studentCode: string
): Promise<GetCurrentClassesByStudentCodeResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<{ studentCode: string }, GetCurrentClassesByStudentCodeResponse>(
    functions,
    "getCurrentClassesByStudentCode"
  );
  const result = await fn({ studentCode: studentCode.trim() });
  return result.data;
};

/** Check if a student code (matrícula) is available before allowing user to continue to class selection. */
export const checkStudentCodeAvailable = async (
  studentCode: string
): Promise<CheckStudentCodeAvailableResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<{ studentCode: string }, CheckStudentCodeAvailableResponse>(
    functions,
    "checkStudentCodeAvailable"
  );
  const result = await fn({ studentCode: studentCode.trim() });
  return result.data;
};

/** Get full registration data to restore the Forum form (e.g. when user clicks back from payment). */
export const getRegistrationForRestore = async (
  registrationId: string
): Promise<GetRegistrationForRestoreResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<
    { registrationId: string },
    GetRegistrationForRestoreResponse
  >(functions, "getRegistrationForRestore");
  const result = await fn({ registrationId: registrationId.trim() });
  return result.data;
};

/** Create Azul Hosted Payment Page session; returns actionUrl and formFields for POST redirect. */
export const createAzulPaymentSession = async (
  registrationId: string
): Promise<CreateAzulPaymentSessionResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<
    { registrationId: string; returnBaseUrl: string },
    CreateAzulPaymentSessionResponse
  >(functions, "createAzulPaymentSession");
  const result = await fn({ registrationId: registrationId.trim(), returnBaseUrl: window.location.origin });
  return result.data;
};

// =============================================================================
// AUTH FUNCTIONS
// =============================================================================

interface CreateUserRequest {
  email: string;
  password: string;
  role: "admin" | "parent";
  /** Display name (e.g. "Alejandro") */
  name?: string;
}

interface CreateUserResponse {
  userId: string;
  email: string;
  role: string;
}

export const createUser = async (data: CreateUserRequest): Promise<CreateUserResponse> => {
  const callable = httpsCallable<CreateUserRequest, CreateUserResponse>(
    functions!,
    "createUser"
  );
  const result = await callable(data);
  return result.data;
};

// =============================================================================
// CLASS FUNCTIONS (Admin)
// =============================================================================

/**
 * Callable only — httpsCallable(functions, "createClass").
 * No URL, no fetch, no manual headers. (Console "api.ts" errors refer to this call.)
 */
export const createClass = async (
  data: CreateClassRequest
): Promise<CreateClassResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<CreateClassRequest, CreateClassResponse>(
    functions,
    "createClass"
  );
  const result = await fn(data);
  return result.data;
};

export const getClassesWithEnrollments = async (): Promise<GetClassesWithEnrollmentsResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<unknown, GetClassesWithEnrollmentsResponse>(
    functions,
    "getClassesWithEnrollments"
  );
  const result = await fn({});
  return result.data;
};

/** Public: active classes for the forum (no auth). Single source of truth from Firebase. */
export const getClassesForForum = async (): Promise<GetClassesForForumResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<unknown, GetClassesForForumResponse>(
    functions,
    "getClassesForForum"
  );
  const result = await fn({});
  return result.data;
};

export const updateClass = async (
  data: UpdateClassRequest
): Promise<UpdateClassResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<UpdateClassRequest, UpdateClassResponse>(
    functions,
    "updateClass"
  );
  const result = await fn(data);
  return result.data;
};

export const deleteClass = async (
  data: DeleteClassRequest
): Promise<DeleteClassResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<DeleteClassRequest, DeleteClassResponse>(
    functions,
    "deleteClass"
  );
  const result = await fn(data);
  return result.data;
};

// =============================================================================
// STUDENTS / PARENTS (Admin) — list only; parent linking removed from UI
// =============================================================================

export const listStudentsForAdmin = async (opts?: { month?: string }): Promise<ListStudentsForAdminResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<{ month?: string }, ListStudentsForAdminResponse>(
    functions,
    "listStudentsForAdmin"
  );
  const result = await fn(opts ?? {});
  return result.data;
};

export const listParentsForAdmin = async (): Promise<ListParentsForAdminResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<unknown, ListParentsForAdminResponse>(
    functions,
    "listParentsForAdmin"
  );
  const result = await fn({});
  return result.data;
};

// =============================================================================
// ENROLLMENT FUNCTIONS
// =============================================================================

export const createEnrollment = async (
  data: CreateEnrollmentRequest
): Promise<CreateEnrollmentResponse> => {
  const callable = httpsCallable<CreateEnrollmentRequest, CreateEnrollmentResponse>(
    functions!,
    "createEnrollment"
  );
  const result = await callable(data);
  return result.data;
};

export const deleteEnrollment = async (
  data: DeleteEnrollmentRequest
): Promise<{ success: boolean }> => {
  const callable = httpsCallable<DeleteEnrollmentRequest, { success: boolean }>(
    functions!,
    "deleteEnrollment"
  );
  const result = await callable(data);
  return result.data;
};

export const createEnrollmentAsStudent = async (
  data: CreateEnrollmentAsStudentRequest
): Promise<CreateEnrollmentAsStudentResponse> => {
  const callable = httpsCallable<
    CreateEnrollmentAsStudentRequest,
    CreateEnrollmentAsStudentResponse
  >(functions!, "createEnrollmentAsStudent");
  const result = await callable(data);
  return result.data;
};

// =============================================================================
// PUBLIC PAYMENT BY REGISTRATION ID (no auth – forum parents use registration ID)
// =============================================================================

/** Pass matrícula (student code); backend resolves to registration. Supports "AS-123" or "123". */
export const getPendingByRegistrationId = async (
  matriculaOrRegistrationId: string
): Promise<GetPendingByRegistrationIdResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<
    { registrationId: string },
    GetPendingByRegistrationIdResponse
  >(functions, "getPendingByRegistrationId");
  const result = await fn({ registrationId: matriculaOrRegistrationId.trim() });
  return result.data;
};

export const updateRegistrationFiscalData = async (
  data: UpdateRegistrationFiscalDataRequest
): Promise<UpdateRegistrationFiscalDataResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<
    UpdateRegistrationFiscalDataRequest,
    UpdateRegistrationFiscalDataResponse
  >(functions, "updateRegistrationFiscalData");
  const result = await fn(data);
  return result.data;
};

/** Switch registration from manual to online payment (so user can pay with Azul). */
export const updateRegistrationPaymentMethod = async (
  data: UpdateRegistrationPaymentMethodRequest
): Promise<UpdateRegistrationPaymentMethodResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<
    UpdateRegistrationPaymentMethodRequest,
    UpdateRegistrationPaymentMethodResponse
  >(functions, "updateRegistrationPaymentMethod");
  const result = await fn(data);
  return result.data;
};

/** Update selected classes for a pending registration (when class is full, etc.). */
export const updateRegistrationClasses = async (
  data: UpdateRegistrationClassesRequest
): Promise<UpdateRegistrationClassesResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<
    UpdateRegistrationClassesRequest,
    UpdateRegistrationClassesResponse
  >(functions, "updateRegistrationClasses");
  const result = await fn(data);
  return result.data;
};

/** Admin: get student and their enrollments (for managing classes). */
export const getStudentWithEnrollments = async (
  studentId: string
): Promise<GetStudentWithEnrollmentsResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<
    { studentId: string },
    GetStudentWithEnrollmentsResponse
  >(functions, "getStudentWithEnrollments");
  const result = await fn({ studentId: studentId.trim() });
  return result.data;
};

/** Backfill enrollments from registration selectedClasses (for paid registrations created before webhook created enrollments). */
export const backfillEnrollmentsForRegistration = async (
  registrationId: string
): Promise<{ created: number }> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<{ registrationId: string }, { created: number }>(
    functions,
    "backfillEnrollmentsForRegistration"
  );
  const result = await fn({ registrationId: registrationId.trim() });
  return result.data;
};

/** Admin: mark a registration as paid manually (creates student and sends email). */
export const markRegistrationPaidManually = async (
  registrationId: string
): Promise<{ registrationId: string }> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<{ registrationId: string }, { registrationId: string }>(
    functions,
    "markRegistrationPaidManually"
  );
  const result = await fn({ registrationId: registrationId.trim() });
  return result.data;
};

/** Admin: open a new month (create pending monthlyPayments for all active registrations). */
export const openMonth = async (
  month: string
): Promise<{ created: number; month: string }> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<{ month: string }, { created: number; month: string }>(
    functions,
    "openMonth"
  );
  const result = await fn({ month: month.trim() });
  return result.data;
};

/** Admin: read current class selection window flag. */
export const getClassSelectionWindow = async (): Promise<ClassSelectionWindowResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<unknown, ClassSelectionWindowResponse>(
    functions,
    "getClassSelectionWindow"
  );
  const result = await fn({});
  return result.data;
};

/** Admin: open or close class selection window for Pagos screen. */
export const setClassSelectionWindow = async (
  open: boolean
): Promise<ClassSelectionWindowResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<{ open: boolean }, ClassSelectionWindowResponse>(
    functions,
    "setClassSelectionWindow"
  );
  const result = await fn({ open });
  return result.data;
};

/** Public: get active registration month (YYYY-MM). Used for Forum header. */
export const getActiveRegistrationMonth = async (): Promise<GetActiveRegistrationMonthResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<unknown, GetActiveRegistrationMonthResponse>(
    functions,
    "getActiveRegistrationMonth"
  );
  const result = await fn({});
  return result.data;
};

/** Admin: set active registration month (YYYY-MM). */
export const setActiveRegistrationMonth = async (
  month: string
): Promise<SetActiveRegistrationMonthResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<{ month: string }, SetActiveRegistrationMonthResponse>(
    functions,
    "setActiveRegistrationMonth"
  );
  const result = await fn({ month });
  return result.data;
};

// =============================================================================
// ADMIN SETTINGS
// =============================================================================

export const getAdminSettings = async (): Promise<GetAdminSettingsResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<unknown, GetAdminSettingsResponse>(
    functions,
    "getAdminSettings"
  );
  const result = await fn({});
  return result.data;
};

export const updateAdminSettings = async (
  updates: Partial<AdminSettings>
): Promise<UpdateAdminSettingsResponse> => {
  if (!functions) throw new Error("Firebase functions not initialized");
  const fn = httpsCallable<Partial<AdminSettings>, UpdateAdminSettingsResponse>(
    functions,
    "updateAdminSettings"
  );
  const result = await fn(updates);
  return result.data;
};

